import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Edit, Trash2, Plus, Globe, Loader2 } from "lucide-react"
import { useBlogTags } from '@/hooks/use-blog-tags'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { BlogTag, blogTagService } from '@/services/other-services'
import { toast } from 'sonner'
import { RefreshCw } from "lucide-react"

const SUPPORTED_LANGUAGES = [
    { code: 'tr', name: 'Türkçe' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'ru', name: 'Русский' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'ar', name: 'العربية' },
]

const localizedStringSchema = z.object({
    tr: z.string().min(2, { message: 'En az 2 karakter olmalıdır' }),
    en: z.string().optional().or(z.literal('')),
    fr: z.string().optional().or(z.literal('')),
    es: z.string().optional().or(z.literal('')),
    ru: z.string().optional().or(z.literal('')),
    nl: z.string().optional().or(z.literal('')),
    ar: z.string().optional().or(z.literal('')),
})

const tagSchema = z.object({
    name: localizedStringSchema,
    slug: localizedStringSchema,
})

type TagFormValues = z.infer<typeof tagSchema>

export function TagList() {
    const { data: tags, isLoading, createTag, updateTag, deleteTag } = useBlogTags()
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isTranslating, setIsTranslating] = useState(false)
    const [editingTag, setEditingTag] = useState<BlogTag | null>(null)

    const form = useForm<TagFormValues>({
        resolver: zodResolver(tagSchema),
        defaultValues: {
            name: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            slug: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' }
        }
    })

    const generateSlug = (text: string) => {
        return text
            .trim()
            .replace(/İ/g, 'i')
            .replace(/I/g, 'i')
            .replace(/ı/g, 'i')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        form.setValue('name.tr', value)
        if (!editingTag) {
            form.setValue('slug.tr', generateSlug(value))
        }
    }

    const handleOpenDialog = (tag?: BlogTag) => {
        if (tag) {
            setEditingTag(tag)
            form.reset({
                name: tag.name,
                slug: tag.slug
            })
        } else {
            setEditingTag(null)
            form.reset({
                name: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                slug: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' }
            })
        }
        setIsDialogOpen(true)
    }

    const onSubmit = async (values: TagFormValues) => {
        if (editingTag) {
            await updateTag({ id: editingTag.id || editingTag._id!, data: values })
        } else {
            await createTag(values)
        }
        setIsDialogOpen(false)
    }

    const handleBulkTranslate = async () => {
        if (!confirm('Tüm etiketler için eksik çeviriler AI ile tamamlanacak. Emin misiniz?')) return

        setIsTranslating(true)
        try {
            const res = await blogTagService.bulkTranslate()
            toast.success(res.message || 'Toplu çeviri başarıyla tamamlandı')
            window.location.reload()
        } catch (error) {
            console.error('Bulk translate error:', error)
            toast.error('Toplu çeviri sırasında bir hata oluştu')
        } finally {
            setIsTranslating(false)
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Etiketler</h2>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={handleBulkTranslate}
                        disabled={isTranslating}
                        className="border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                    >
                        {isTranslating ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="mr-2 h-4 w-4" />
                        )}
                        Etiketleri Çevir
                    </Button>
                    <Button onClick={() => handleOpenDialog()}>
                        <Plus className="mr-2 h-4 w-4" /> Yeni Etiket
                    </Button>
                </div>
            </div>

            <Card>
                <CardContent className="pt-6">
                    {isLoading ? (
                        <div className="flex h-32 items-center justify-center">Yükleniyor...</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>İsim (TR)</TableHead>
                                    <TableHead>Slug (TR)</TableHead>
                                    <TableHead className="text-right">İşlemler</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {tags?.map((tag) => (
                                    <TableRow key={tag.id || tag._id} className="group cursor-pointer">
                                        <TableCell className="font-medium group-hover:text-primary transition-colors">
                                            {typeof tag.name === 'object' ? tag.name.tr : tag.name}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {typeof tag.slug === 'object' ? tag.slug.tr : tag.slug}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(tag)}>
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => deleteTag(tag.id || tag._id!)} className="text-destructive">
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingTag ? 'Etiketi Düzenle' : 'Yeni Etiket Ekle'}</DialogTitle>
                    </DialogHeader>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            <div className="relative w-full rounded-lg border px-4 py-3 text-sm flex items-start gap-3 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300">
                                <Globe className="h-4 w-4 mt-0.5" />
                                <div>
                                    <p className="font-medium mb-1 flex items-center gap-1">✨ Yapay Zeka Çevirisi Aktif</p>
                                    Türkçe etiket adı baz alınarak tüm diller otomatik olarak çevrilecektir. Bu işlem kısa bir süre vakit alabilir, lütfen bekleyiniz.
                                </div>
                            </div>

                            <Tabs defaultValue="tr" className="w-full">
                                <TabsList className="grid w-full grid-cols-4 lg:grid-cols-7 h-auto p-1 bg-muted/50">
                                    {SUPPORTED_LANGUAGES.map((lang) => (
                                        <TabsTrigger key={lang.code} value={lang.code} className="py-2 text-xs">
                                            {lang.name}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>

                                {SUPPORTED_LANGUAGES.map((lang) => (
                                    <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <FormField
                                                control={form.control}
                                                name={`name.${lang.code}` as any}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Etiket Adı ({lang.name})</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                value={(field.value as string) || ''}
                                                                onChange={lang.code === 'tr' ? handleNameChange : field.onChange}
                                                                placeholder={`${lang.name} isim`}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name={`slug.${lang.code}` as any}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Slug ({lang.name})</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                value={(field.value as string) || ''}
                                                                placeholder="etiket-slug"
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    </TabsContent>
                                ))}
                            </Tabs>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>İptal</Button>
                                <Button type="submit" disabled={form.formState.isSubmitting}>
                                    {form.formState.isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Yapay zeka çevirileri yapılıyor, lütfen bekleyin...
                                        </>
                                    ) : editingTag ? (
                                        'Güncelle'
                                    ) : (
                                        'Oluştur'
                                    )}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
        </div>
    )
}
